import { DownloadIcon } from "@components/Icons";
import TableHeaderControls from "@components/Project/TableHeaderControls";
import AdvancedSearch from "@components/Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { resolveSearchCriteria } from "@utils/searchHelpers";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { ConfirmDeleteModal } from "@components/UI";
import queryClient from "@configs/react-query/query-client";
import queryKeys from "@configs/react-query/query-key";
import { showErrorToast, showSuccessToast } from "@configs/react-toastify/snackbar";
import { LICENSE_TYPE_OPTIONS_NO_TRANSLATION } from "@constants/openSource";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDate, uppercase, isNotNullAndEmptyArray } from "@utils/common";
import { refreshPaginatedListAfterDelete } from "@utils/pagination";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useRoutePermissions } from "@contexts/RoutePermissionContext";
import { ComponentHeader } from "@components/Tables/ComponentHeader";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRightArrowLeft } from "@fortawesome/free-solid-svg-icons";

export default function ComponentList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasDeletePermission } = useRoutePermissions();
  const { colors } = useTheme();
    const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [debouncedSearchFilters, setDebouncedSearchFilters] = useState(searchFilters);
  const debounceTimerRef = useRef(null);
  const lastSearchChangeSourceRef = useRef(null); // "searchText" | "other"
  const [isSearching, setIsSearching] = useState(false); // only for search box typing
  const [componentList, setComponentList] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.COMPONENT_LIST, DEFAULT_ITEMS_PER_PAGE);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const getListData = useMutationData(
    queryKeys.getListComponents,
    { method: "POST" },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          const list = response.object || [];
          const totalCount = response.objectCount || 0;
          setComponentList(list);
          setTotalData(totalCount);
          setTotalPages(Math.ceil(totalCount / (itemsPerPage || 1)));
        }
      },
      onError: (error) => {
        console.error("Error fetching components:", error);
        showErrorToast(t("open-source.get-component-data-error"));
      },
    },
  );

  const searchFields = [
    {
      name: "licenseName",
      label: t("open-source.license-name"),
      placeholder: t("open-source.license-name-placeholder"),
    },
    {
      name: "licenseTypeCd",
      label: t("open-source.license-type"),
      type: "select",
      placeholder: t("license-type-options.placeholder"),
      options: [
        ...LICENSE_TYPE_OPTIONS_NO_TRANSLATION.map((option) => ({
          value: option.value,
          label: t(option.label),
        })),
      ],
    },
    {
      name: "description",
      label: t("open-source.description"),
      placeholder: t("open-source.description-placeholder"),
    },
  ];
  const { showAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: searchFields,
    quickSearchField: "searchText",
    setSearchCriteria: setSearchFilters,
  });

  const fetchData = useCallback(() => {
    if (getListData.isPending) {
      return;
    }

    const effectiveCriteria = resolveSearchCriteria(debouncedSearchFilters, {
      quickSearchField: "searchText",
      isAdvancedPanelOpen: showAdvancedSearch,
    });

    const bodyRequest = Object.keys(effectiveCriteria).reduce((acc, key) => {
      let value = effectiveCriteria[key];
      if (typeof value === "string") {
        value = value.trim();
      }
      if (value !== "" && value !== undefined && value !== null) {
        if (key === "createdAtFrom" || key === "createdAtTo") {
          acc[key] = formatDate(value);
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {});

    bodyRequest.pageSize = itemsPerPage;
    bodyRequest.startRow = itemsPerPage * (currentPage - 1);

    getListData.mutate(bodyRequest);
  }, [debouncedSearchFilters, currentPage, itemsPerPage, getListData.isPending, showAdvancedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchFilters]);

  // Debounce search input to avoid calling API too frequently
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (lastSearchChangeSourceRef.current === "searchText") {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchFilters(searchFilters);
      setIsSearching(false);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchFilters]);

  useEffect(() => {
    fetchData();
  }, [currentPage, itemsPerPage, debouncedSearchFilters, showAdvancedSearch]);

  // Listen for genesis_import_complete event to refresh project list
  useEffect(() => {
    const handleGenesisImportComplete = (event) => {
      const entities = event?.detail?.entities;
      if (entities?.length && !entities.includes("components")) {
        return;
      }
      fetchData();
    };

    window.addEventListener('genesis_import_complete', handleGenesisImportComplete);
    return () => {
      window.removeEventListener('genesis_import_complete', handleGenesisImportComplete);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = ["componentName", "licenseName", "cpe", "description", "homepageUrl", "downloadUrl"];

  const columnConfig = {
    componentName: {
      style: {
        width: "25%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => <ComponentHeader />,
      customRender: (value, row) => (
        <div>
          <div className="text-sm">
            {value || "-"} ({row.componentVersion})
          </div>
          <div className={`flex items-center gap-1 text-xs ${colors.textNeutralMuted}`}>
            {row.hasLicensePeriodChange && (
              <span
                className="inline-flex flex-shrink-0"
                title={t("open-source.license-period-change-indicator")}
                aria-label={t("open-source.license-period-change-indicator")}
              >
                <FontAwesomeIcon
                  icon={faArrowRightArrowLeft}
                  className={`w-3 h-3 ${colors.textLicensePeriodAccent}`}
                />
              </span>
            )}
            <span>{row.licenseIdentifier || "-"}</span>
          </div>
        </div>
      ),
    },
    licenseName: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.license-name"),
    },
    cpe: {
      style: {
        width: "15%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.cpe"),
    },
    description: {
      style: {
        width: "20%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.description"),
    },
    homepageUrl: {
      style: {
        width: "10%",
        whiteSpace: "normal",
        overflowWrap: "initial",
      },
      headerRender: () => t("open-source.homepage-url"),
      customRender: (value) =>
        value ? (
          <div style={{ textAlign: "center" }} onClick={(event) => event.stopPropagation()}>
            <a href={value} target="_blank" rel="noopener noreferrer">
              <p className={colors.textActive}>{value}</p>
            </a>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>-</div>
        ),
    },
    downloadUrl: {
      style: {
        width: "10%",
        whiteSpace: "normal",
      },
      headerRender: () => t("open-source.download"),
      customRender: (value) =>
        value ? (
          <div onClick={(event) => event.stopPropagation()}>
            <a href={value} target="_blank" rel="noopener noreferrer">
              <DownloadIcon className="w-6 h-6" />
            </a>
          </div>
        ) : (
          <div className="flex justify-center items-center">-</div>
        ),
    },
  };

  const handleDeleteComponent = () => {
    setShowDeleteModal(true);
  };

  const deleteData = useMutationData(
    queryKeys.deleteComponents,
    {
      method: "DELETE",
    },
    {
      onSuccess: async () => {
        refreshPaginatedListAfterDelete({
          currentPage,
          setCurrentPage,
          itemsPerPage,
          totalData,
          deletedCount: selectedRows.length,
          fetchData,
        });
        showSuccessToast(selectedRows.length + t("open-source.component-delete-success"));
        setSelectedRows([]);
        queryClient.invalidateQueries(queryKeys.getListComponents);
        queryClient.invalidateQueries(queryKeys.getProjectDetail);
      },
      onError: async (error) => {
        setShowDeleteModal(false);
        if (isNotNullAndEmptyArray(error.response.data?.deletedSeqs)) {
          showSuccessToast(error.response.data.deletedSeqs.length + t("open-source.component-delete-success"));
          setSelectedRows([]);
          fetchData();
        }
        if (error.response.status === 409) {
          showErrorToast(t("status.error"), error?.response?.data?.message + t("open-source.component-delete-error-conflict"));
        } else {
          showErrorToast(t("status.error"), t("open-source.component-delete-error"));
        }
      },
    },
  );

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const bodyRequest = selectedRows;
      await deleteData.mutateAsync(bodyRequest);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Error deleting components:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  const handleAddComponent = () => {
    navigate("./new");
  };

  const handleCheckboxChange = (newSelectedRows) => {
    setSelectedRows([...newSelectedRows]);
  };

  const onRowClick = (rowData) => {
    navigate(`./${rowData.componentSeq}`);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const searchHandler = useCallback((inputData) => {
    if (Object.prototype.hasOwnProperty.call(inputData || {}, "searchText")) {
      lastSearchChangeSourceRef.current = "searchText";
    } else {
      lastSearchChangeSourceRef.current = "other";
    }
    setSearchFilters((prev) => ({
      ...prev,
      ...inputData,
    }));
  }, []);

  return (
    <div className={`flex flex-col ${colors.bgSecondary}`} data-testid="component-list-page">
      {/* Header */}
      <div className={`${colors.textPlaceholder}`}>
        <div className="flex items-center justify-between">
          <h1 className={`text-[28px] uppercase font-bold ${colors.textPrimary}`}>{t("open-source.component-list-title")}</h1>
        </div>
      </div>

      {/* Controls */}
      <div className={`flex-shrink-0 pt-4 pr-4 ${colors.bgSecondary}`}>
        <TableHeaderControls
          onAdvancedSearchToggle={handleAdvancedSearchToggle}
          onDeleteProject={handleDeleteComponent}
          onAdd={handleAddComponent}
          onSearch={searchHandler}
          hasSelectedRows={selectedRows.length > 0}
          searchField="searchText"
          searchValue={searchFilters.searchText ?? ""}
          placeholderSearch={t("common.search-placeholder")}
          titleAddButton={t("open-source.add-component-button")}
          titleDeleteButton={t("open-source.delete-components-button")}
          isDisplayDeleteButton={hasDeletePermission}
          isSearching={isSearching}
          searchInputTestId="component-list-search-input"
          addButtonTestId="component-list-add-button"
        />
      </div>

      {/* Advanced Search */}
      {showAdvancedSearch && (
        <div className="mb-4" data-testid="component-list-advanced-search">
          <AdvancedSearch
            title={t("common.advanced-search")}
            searchFields={[...searchFields]}
            searchHandler={searchHandler}
            withoutSearch={true}
          />
        </div>
      )}

      {/* Component Table */}
      <div className="flex-grow min-h-0" data-testid="component-list-table">
        <TableWithPagination
          columnConfig={columnConfig}
          data={componentList}
          columns={columns}
          onRowClick={onRowClick}
          loading={getListData.isPending || isSearching}
          totalPages={totalPages}
          totalData={totalData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          maxRowsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChangeWithReset}
          selectedRows={selectedRows}
          onCheckboxChange={handleCheckboxChange}
          disabledRow={[]}
          hasCheckbox={hasDeletePermission}
          hasRadioButton={false}
          rowKey="componentSeq"
          shouldShowGoToPage={true}
          showItemsPerPageSelector={true}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        data-testid="component-list-delete-modal"
        isOpen={showDeleteModal}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={uppercase(t("open-source.delete-components-button"))}
        description={t("common.delete-modal-body")}
        loading={deleteLoading}
      />
    </div>
  );
}
